import React, { useState, useEffect } from "react";
import { useSelector, useDispatch } from "react-redux";
import {
  fetchEmployees,
  updateEmployee,
} from "../redux/actions/employeeActions";
import { fetchDesignations } from "../redux/actions/designationActions";
import ExcelJS from "exceljs";
import theme from "../components/shared/theme";
import {
  dropdownStyle,
  thStyle,
  tdStyle,
  paginationButtonStyle,
} from "../components/shared/themeStyles";
import { FiChevronLeft, FiChevronRight } from "react-icons/fi";
import EditEmployeeModal from "../pages/employee_management/EditEmployeeModal";
import ChangeStatusDropdown from "../pages/employee_management/ChangeStatusDropdown";
import {
  fetchDistricts,
  fetchMunicipalities,
} from "../redux/actions/locationActions";

const Employee = () => {
  const dispatch = useDispatch();
  const { employees, loading, pagination } = useSelector(
    (state) => state.employee,
  );
  const [currentPage, setCurrentPage] = useState(1);
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedDistrict, setSelectedDistrict] = useState("");
  const [selectedMunicipality, setSelectedMunicipality] = useState("");
  const [selectedWard, setSelectedWard] = useState("");
  const [isEditOpen, setIsEditOpen] = useState(false);
  const [editEmployee, setEditEmployee] = useState(null);
  const [selectedDesignation, setSelectedDesignation] = useState("");
  const [selectedStatus, setSelectedStatus] = useState("");
  const [sortField, setSortField] = useState("");
  const [sortOrder, setSortOrder] = useState("asc");
  const [limit, setLimit] = useState(20);
  const [designations, setDesignations] = useState([]);
  const [designationSearchTerm, setDesignationSearchTerm] = useState("");
  const [isDesignationDropdownOpen, setIsDesignationDropdownOpen] =
    useState(false);
  const [downloadingExcel, setDownloadingExcel] = useState(false);

  const { districts, municipalities } = useSelector((state) => state.location);
  const { designations: designationList } = useSelector(
    (state) => state.designation,
  );

  useEffect(() => {
    setSelectedMunicipality("");
  }, [selectedDistrict]);

  useEffect(() => {
    dispatch(fetchDistricts());
    dispatch(fetchMunicipalities());
    dispatch(fetchDesignations());
  }, [dispatch]);

  const selectedDistrictObj = districts.find(
    (d) => d.name === selectedDistrict,
  );
  const selectedDistrictId = selectedDistrictObj?.id;

  const filteredMunicipalities = selectedDistrictId
    ? municipalities.filter((m) => m.district_id === selectedDistrictId)
    : municipalities;

  const selectedMunicipalityObj = filteredMunicipalities.find(
    (m) => m.name === selectedMunicipality,
  );
  const selectedMunicipalityId = selectedMunicipalityObj?.id;

  useEffect(() => {
    const params = {
      page: currentPage,
      limit: limit,
      ...(selectedDistrictId && { district_id: selectedDistrictId }),
      ...(selectedMunicipalityId && {
        municipality_id: selectedMunicipalityId,
      }),
      ...(selectedStatus && { status: selectedStatus }),
      ...(selectedDesignation && { designation: selectedDesignation }),
      ...(searchTerm && { search: searchTerm }),
    };

    dispatch(fetchEmployees(params));
  }, [
    currentPage,
    limit,
    selectedDistrictId,
    selectedMunicipalityId,
    selectedStatus,
    selectedDesignation,
    searchTerm,
    dispatch,
  ]);

  const currentItems = employees;
  const totalPages = pagination?.last_page || 1;

  const toggleSort = (field) => {
    if (sortField === field) {
      setSortOrder(sortOrder === "asc" ? "desc" : "asc");
    } else {
      setSortField(field);
      setSortOrder("asc");
    }
  };

  // ---------- Excel Export (colored) ----------
  const handleDownloadExcel = async () => {
    if (downloadingExcel) return;
    setDownloadingExcel(true);
    try {
      const token = localStorage.getItem("token");

      // Build query params exactly like the fetchEmployees action
      const queryParams = new URLSearchParams({
        page: 1,
        limit: 100000,
        ...(selectedDistrictId && { district_id: selectedDistrictId }),
        ...(selectedMunicipalityId && { municipality_id: selectedMunicipalityId }),
        ...(selectedStatus && { status: selectedStatus }),
        ...(selectedDesignation && { designation: selectedDesignation }),
        ...(searchTerm && { search: searchTerm }),
      }).toString();

      const response = await fetch(
        `${process.env.REACT_APP_API_BASE_URL}/v1/api/getAllEmployee?${queryParams}`,
        {
          headers: { authorization: token },
        }
      );
      if (!response.ok) throw new Error("Failed to fetch data");
      const data = await response.json();
      const records = data?.data || [];

      const workbook = new ExcelJS.Workbook();
      const worksheet = workbook.addWorksheet("Employees", {
        views: [{ state: "frozen", ySplit: 1 }],
      });

      // Define columns
      worksheet.columns = [
        { header: "ID", key: "id", width: 8 },
        { header: "Name", key: "name", width: 22 },
        { header: "Email", key: "email", width: 25 },
        { header: "Phone", key: "phone", width: 14 },
        { header: "Designation", key: "designation", width: 18 },
        { header: "District", key: "district", width: 14 },
        { header: "Municipality", key: "municipality", width: 16 },
        { header: "DOB", key: "dob", width: 12 },
        { header: "DOJ", key: "doj", width: 12 },
        { header: "Photo URL", key: "photo", width: 30 },
        { header: "Status", key: "status", width: 12 },
      ];

      // Style header
      worksheet.getRow(1).font = { bold: true, color: { argb: "FFFFFFFF" } };
      worksheet.getRow(1).fill = {
        type: "pattern",
        pattern: "solid",
        fgColor: { argb: "FF333333" },
      };
      worksheet.getRow(1).alignment = { horizontal: "center", vertical: "middle" };

      // Add data
      records.forEach((emp, idx) => {
        const row = worksheet.addRow({
          id: emp.id,
          name: emp.name || "",
          email: emp.email || "",
          phone: emp.phone || "",
          designation: emp.designation || "",
          district: emp.district || "",
          municipality: emp.municipality || "",
          dob: emp.dob ? new Date(emp.dob).toLocaleDateString("en-IN") : "",
          doj: emp.doj ? new Date(emp.doj).toLocaleDateString("en-IN") : "",
          photo: emp.photo || "",
          status: emp.status || "",
        });

        // Alternate row shading
        if (idx % 2 === 1) {
          row.eachCell((cell) => {
            cell.fill = {
              type: "pattern",
              pattern: "solid",
              fgColor: { argb: "FFF5F5F5" },
            };
          });
        }

        // Color status cell
        const statusCell = row.getCell("status");
        if (emp.status === "active") {
          statusCell.font = { color: { argb: "FF2E7D32" }, bold: true };
        } else if (emp.status === "inactive") {
          statusCell.font = { color: { argb: "FFC62828" }, bold: true };
        } else if (emp.status === "suspended") {
          statusCell.font = { color: { argb: "FFED6C02" }, bold: true };
        }

        row.alignment = { vertical: "middle" };
      });

      // Auto filter
      worksheet.autoFilter = {
        from: "A1",
        to: `K${worksheet.rowCount}`,
      };

      const buffer = await workbook.xlsx.writeBuffer();
      const blob = new Blob([buffer], {
        type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
      });
      const url = URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = `Employees_Report_${new Date().toISOString().slice(0, 10)}.xlsx`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
    } catch (err) {
      console.error("Excel download error:", err);
      alert("Failed to download Excel. Please try again.");
    } finally {
      setDownloadingExcel(false);
    }
  };

  const handleEditClick = (emp) => {
    setEditEmployee(emp);
    setIsEditOpen(true);
  };

  const handleFieldChange = (field, value) => {
    setEditEmployee((prev) => ({ ...prev, [field]: value }));
  };

  const handleUpdateSubmit = async (e) => {
    e.preventDefault();
    try {
      await dispatch(updateEmployee(editEmployee));
      setIsEditOpen(false);
      dispatch(fetchEmployees());
    } catch {}
  };

  return (
    <div
      style={{
        padding: theme.spacing.lg,
        backgroundColor: theme.colors.background,
        minHeight: "100vh",
      }}
    >
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          marginBottom: theme.spacing.lg,
        }}
      >
        <h1
          style={{
            fontSize: theme.font.size["3xl"],
            fontWeight: theme.font.weight.bold,
            color: theme.colors.grayDark,
          }}
        >
          Employee Management
        </h1>
      </div>

      {/* Filters */}
      <div style={{ marginBottom: theme.spacing.lg }}>
        <select
          value={selectedDistrict}
          onChange={(e) => setSelectedDistrict(e.target.value)}
          style={dropdownStyle}
        >
          <option value="">All Districts</option>
          {districts.map((d) => (
            <option key={d.id} value={d.name}>
              {d.name}
            </option>
          ))}
        </select>

        <select
          value={selectedMunicipality}
          onChange={(e) => setSelectedMunicipality(e.target.value)}
          style={dropdownStyle}
        >
          <option value="">All Municipalities</option>
          {filteredMunicipalities.map((m) => (
            <option key={m.id} value={m.name}>
              {m.name}
            </option>
          ))}
        </select>

        <div style={{ position: "relative", display: "inline-block" }}>
          <input
            type="text"
            placeholder="Search Designation..."
            value={designationSearchTerm}
            onClick={() =>
              setIsDesignationDropdownOpen(!isDesignationDropdownOpen)
            }
            onChange={(e) => {
              setDesignationSearchTerm(e.target.value);
              setIsDesignationDropdownOpen(true);
            }}
            style={{
              padding: theme.spacing.sm,
              border: "1px solid #ccc",
              borderRadius: theme.radius.md,
              minWidth: "200px",
              ...dropdownStyle,
            }}
          />
          {isDesignationDropdownOpen && (
            <div
              style={{
                position: "absolute",
                top: "100%",
                left: 0,
                right: 0,
                maxHeight: "200px",
                overflowY: "auto",
                backgroundColor: "#fff",
                border: "1px solid #ccc",
                borderRadius: theme.radius.md,
                zIndex: 1000,
                marginTop: "4px",
              }}
            >
              <div
                onClick={() => {
                  setSelectedDesignation("");
                  setDesignationSearchTerm("");
                  setIsDesignationDropdownOpen(false);
                }}
                style={{
                  padding: theme.spacing.sm,
                  cursor: "pointer",
                }}
              >
                All Designations
              </div>
              {designationList
                .filter((d) =>
                  d.toLowerCase().includes(designationSearchTerm.toLowerCase()),
                )
                .map((d) => (
                  <div
                    key={d}
                    onClick={() => {
                      setSelectedDesignation(d);
                      setDesignationSearchTerm(d);
                      setIsDesignationDropdownOpen(false);
                    }}
                    style={{
                      padding: theme.spacing.sm,
                      cursor: "pointer",
                      backgroundColor:
                        selectedDesignation === d ? "#e3f2fd" : "transparent",
                    }}
                  >
                    {d}
                  </div>
                ))}
            </div>
          )}
        </div>
        <select
          value={selectedStatus}
          onChange={(e) => setSelectedStatus(e.target.value)}
          style={dropdownStyle}
        >
          <option value="">All Status</option>
          <option value="active">Active</option>
          <option value="inactive">Inactive</option>
          <option value="suspended">Suspended</option>
        </select>
        <input
          type="text"
          placeholder="Search by name, email, phone or designation"
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          style={{
            padding: theme.spacing.sm,
            marginLeft: theme.spacing.sm,
            border: "1px solid #ccc",
            borderRadius: theme.radius.md,
            minWidth: "250px",
          }}
        />
        <select
          value={limit}
          onChange={(e) => {
            setLimit(Number(e.target.value));
            setCurrentPage(1);
          }}
          style={{
            ...dropdownStyle,
            marginLeft: theme.spacing.sm,
            minWidth: "80px",
          }}
        >
          {[10, 20, 50, 100, 200, 500, 800, 1000, 2000].map((num) => (
            <option key={num} value={num}>
              {num}
            </option>
          ))}
        </select>

        {/* Excel Export Button */}
        <button
          onClick={handleDownloadExcel}
          disabled={downloadingExcel}
          style={{
            backgroundColor: downloadingExcel ? "#aaa" : "#28a745",
            color: "#fff",
            padding: theme.spacing.sm,
            borderRadius: theme.radius.md,
            border: "none",
            cursor: downloadingExcel ? "not-allowed" : "pointer",
            fontWeight: "bold",
            marginLeft: theme.spacing.sm,
            minWidth: "100px",
            display: "inline-flex",
            alignItems: "center",
            justifyContent: "center",
            gap: "8px",
          }}
        >
          {downloadingExcel ? (
            <>
              <div className="spinner-small" /> Excel...
            </>
          ) : (
            <>
              <span>📊</span> Excel
            </>
          )}
        </button>
      </div>

      {/* Downloading overlay */}
      {downloadingExcel && (
        <div
          style={{
            position: "fixed",
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: "rgba(255,255,255,0.85)",
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
            zIndex: 9999,
          }}
        >
          <div className="spinner" />
          <p
            style={{
              marginTop: theme.spacing.md,
              fontSize: theme.font.size.md,
              fontWeight: "bold",
              color: theme.colors.grayDark,
            }}
          >
            Generating Excel... Please wait
          </p>
        </div>
      )}

      {/* Table (unchanged) */}
      <div
        style={{
          backgroundColor: "#fff",
          borderRadius: theme.radius.lg,
          boxShadow: theme.shadow.soft,
          overflowX: "auto",
        }}
      >
        <table
          style={{
            width: "100%",
            borderCollapse: "collapse",
            fontSize: theme.font.size.sm,
          }}
        >
          <thead
            style={{ backgroundColor: theme.colors.grayDark, color: "#fff" }}
          >
            <tr>
              {[
                "id",
                "photo",
                "name",
                "email",
                "phone",
                "designation",
                "district",
                "municipality",
                "dob",
                "doj",
                "status",
                "actions",
              ].map((field) => (
                <th
                  key={field}
                  style={thStyle}
                  onClick={() => toggleSort(field)}
                >
                  {field.toUpperCase()}
                  {sortField === field
                    ? sortOrder === "asc"
                      ? " ↑"
                      : " ↓"
                    : ""}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {currentItems.map((emp) => (
              <tr key={emp.id} style={{ borderBottom: "1px solid #eee" }}>
                <td style={tdStyle}>{emp.id}</td>
                <td style={tdStyle}>
                  <img
                    src={
                      emp.photo ||
                      "https://img.freepik.com/free-vector/blue-circle-with-white-user_78370-4707.jpg"
                    }
                    alt="Profile"
                    style={{
                      height: "40px",
                      width: "40px",
                      borderRadius: theme.radius.full,
                    }}
                  />
                </td>
                <td style={tdStyle}>{emp.name}</td>
                <td style={tdStyle}>{emp.email}</td>
                <td style={tdStyle}>{emp.phone}</td>
                <td style={tdStyle}>{emp.designation}</td>
                <td style={tdStyle}>{emp.district}</td>
                <td style={tdStyle}>{emp.municipality}</td>
                <td style={tdStyle}>
                  {emp.dob
                    ? new Date(emp.dob).toLocaleDateString("en-IN")
                    : "-"}
                </td>
                <td style={tdStyle}>
                  {emp.doj
                    ? new Date(emp.doj).toLocaleDateString("en-IN")
                    : "-"}
                </td>
                <td style={tdStyle}>
                  <ChangeStatusDropdown
                    userId={emp.id}
                    currentStatus={emp.status}
                  />
                </td>
                <td style={tdStyle}>
                  <button
                    onClick={() => handleEditClick(emp)}
                    style={{
                      backgroundColor: theme.colors.primary,
                      color: "#fff",
                      padding: "4px 8px",
                      borderRadius: theme.radius.sm,
                    }}
                  >
                    Edit
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Pagination */}
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          marginTop: theme.spacing.md,
        }}
      >
        <button
          onClick={() => currentPage > 1 && setCurrentPage(currentPage - 1)}
          disabled={currentPage === 1}
          style={paginationButtonStyle}
        >
          <FiChevronLeft /> Previous
        </button>
        <span style={{ fontSize: theme.font.size.sm }}>
          Page {currentPage} of {totalPages}
        </span>
        <button
          onClick={() =>
            currentPage < totalPages && setCurrentPage(currentPage + 1)
          }
          disabled={currentPage === totalPages}
          style={paginationButtonStyle}
        >
          Next <FiChevronRight />
        </button>
      </div>

      {/* Edit Modal */}
      {isEditOpen && editEmployee && (
        <EditEmployeeModal
          employee={editEmployee}
          onChange={handleFieldChange}
          onCancel={() => setIsEditOpen(false)}
          onSubmit={handleUpdateSubmit}
        />
      )}

      <style>{`
        @keyframes spin {
          0% { transform: rotate(0deg); }
          100% { transform: rotate(360deg); }
        }
        .spinner {
          width: 40px; height: 40px;
          border: 3px solid #e0e0e0;
          border-top-color: #007bff;
          border-radius: 50%;
          animation: spin 1s linear infinite;
        }
        .spinner-small {
          width: 16px; height: 16px;
          border: 2px solid #ffffff;
          border-top-color: transparent;
          border-radius: 50%;
          animation: spin 1s linear infinite;
        }
      `}</style>
    </div>
  );
};

export default Employee;